import { Component, Inject, OnDestroy } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FileLogger } from "@comunicare/comunicare-helper";
import { DISABLE_REASON, IPatientUser, SUB_STATUS, SUB_STATUS_VALUES } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { IPatientDeactivationState, IPatientDeactivationUpdate } from "src/app/models/patient-deactivation.model";
import { PatientService } from "src/app/providers/patient.service";
import { SessionService } from "src/app/providers/session.service";

/**
 * Changes what the temporary deactivation a patient is under is made of - its sub status and the
 * day he is expected to come back - without ending it. The day it started is not part of it: a
 * deactivation that is already running cannot be made to have started on another day.
 */
@Component({
  selector: "app-patient-deactivation-update-dialog",
  templateUrl: "./patient-deactivation-update-dialog.component.html",
  styleUrls: ["./patient-deactivation-update-dialog.component.scss"],
})
export class PatientDeactivationUpdateDialogComponent implements OnDestroy {
  private onDestroy$ = new Subject<void>();
  public reasons = DISABLE_REASON;
  /** DEATH is intentionally left out: this dialog only ever updates a temporary deactivation. */
  public subStatusOptions = SUB_STATUS_VALUES;
  public selectedSubStatus: SUB_STATUS;
  /** What the practitioner typed when he picked the "other" reason. */
  public otherReason = "";
  public endDate: moment.Moment = null;
  /** Day the deactivation started, null for one traced without any. */
  public startDate: moment.Moment = null;
  /** The patient cannot come back before the day after the deactivation started. */
  public minEndDate: moment.Moment;
  private initialSubStatus: SUB_STATUS;
  private initialEndDate: moment.Moment = null;
  private initialOtherReason: string;
  /** Whether the practitioner already tried to validate once - the missing reason error only shows up from then on. */
  private attemptedSubmit = false;

  constructor(
    private patientService: PatientService,
    private sessionService: SessionService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private dialogRef: MatDialogRef<PatientDeactivationUpdateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { patient: IPatientUser; deactivation: IPatientDeactivationState }
  ) {
    // Safe: this dialog only ever opens for a temporary deactivation, so DEATH cannot be here -
    // IPatientDeactivationState.subStatus stays wider only to also display a permanent one's.
    this.initialSubStatus = this.data.deactivation?.subStatus as SUB_STATUS;
    this.selectedSubStatus = this.initialSubStatus;
    this.startDate = this.asMoment(this.data.deactivation?.date);
    this.minEndDate = (this.startDate ?? moment()).clone().add(1, "day");
    this.initialEndDate = this.asMoment(this.data.deactivation?.predictedEndDate);
    this.endDate = this.initialEndDate;
    this.initialOtherReason = this.data.deactivation?.reasonDetail ?? "";
    this.otherReason = this.initialOtherReason;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /** Nothing is sent as long as the practitioner did not actually change something. */
  public get canSubmit(): boolean {
    if (this.endDate && (!this.endDate.isValid() || this.endDate.isBefore(this.minEndDate, "day"))) {
      return false;
    }
    return this.hasSubStatusChanged || this.hasEndDateChanged || this.hasOtherReasonChanged;
  }

  private get hasSubStatusChanged(): boolean {
    return Boolean(this.selectedSubStatus) && this.selectedSubStatus !== this.initialSubStatus;
  }

  private get hasEndDateChanged(): boolean {
    if (!this.endDate?.isValid()) {
      return false;
    }
    return !this.initialEndDate || !this.endDate.isSame(this.initialEndDate, "day");
  }

  /** Whether the "other" reason detail was typed differently from what it started as. */
  private get hasOtherReasonChanged(): boolean {
    return this.selectedSubStatus === this.reasons.OTHER && this.otherReason !== this.initialOtherReason;
  }

  /** Shown once the practitioner tried to validate the "other" reason while leaving it blank. */
  public get otherReasonMissing(): boolean {
    return this.attemptedSubmit && this.selectedSubStatus === this.reasons.OTHER && !this.otherReason;
  }

  public onSubStatusChange(): void {
    if (this.selectedSubStatus !== this.reasons.OTHER) {
      // Drop what was typed for "other" so it cannot be sent along another reason.
      this.otherReason = "";
    }
  }

  public onValidate(): void {
    this.attemptedSubmit = true;
    if (this.otherReasonMissing) {
      return;
    }
    const update = this.buildUpdate();
    this.patientService
      .updateDeactivationStatus(this.data.patient?.patient?._id, update)
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          this.sessionService.needRefreshPatientUser();
          const msg = this.translateService.instant("common.success");
          this.snackBar.open(msg, "ok", { duration: 3000 });
          this.dialogRef.close(update);
        },
        error: (err) => {
          // The deactivation is left untouched: stay open so the practitioner can try again.
          FileLogger.error("PatientDeactivationUpdateDialogComponent", "onValidate", err);
          const msg = this.translateService.instant("common.updateFail");
          this.snackBar.open(msg, "ok", { duration: 3000 });
        },
      });
  }

  private buildUpdate(): IPatientDeactivationUpdate {
    return {
      // The server only reads the reason detail when the sub status travels along as OTHER, so it
      // has to be sent even when OTHER is not new, otherwise an edited detail would not be saved.
      subStatus: this.hasSubStatusChanged || this.hasOtherReasonChanged ? this.selectedSubStatus : undefined,
      // Changing the sub status rewrites the whole deactivation, so the end date has to travel
      // along even when it was not touched, otherwise the server would drop it.
      predictedEndDate: this.endDate?.isValid() ? this.endDate.format() : this.data.deactivation?.predictedEndDate,
      reasonDetail: this.selectedSubStatus === this.reasons.OTHER && this.otherReason ? this.otherReason : undefined,
    };
  }

  private asMoment(date: string): moment.Moment | null {
    if (!date) {
      return null;
    }
    const parsed = moment(date);
    return parsed.isValid() ? parsed : null;
  }
}
